import { BodyMediumMidEmphasis, TitleSmallEmphasized } from '@/design-system';
import colors from '@/theme/colors';
import { css } from '@emotion/css';
import { useIsMobile, useSchools } from '@/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const JarHeaderSubscriptions: React.FC = () => {
  const { subscriptions, hasMultipleChildren } = useSchools();
  const schools = subscriptions.filter(s => s.type === 'school');
  const isMobile = useIsMobile();

  const schoolName = hasMultipleChildren ? 'My Schools' : schools.length > 0 ? schools[0].name : '';

  const [maxChars, setMaxChars] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const avgCharWidth = isMobile ? 10 : 14;
      const maxChars = Math.floor(width / avgCharWidth);
      setMaxChars(maxChars);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  const getVisibleNames = useCallback(() => {
    const slicedSchools = schools.slice(0, 2);

    if (!isMobile) return slicedSchools;

    const schoolNamesLength = slicedSchools.reduce((sum, s) => sum + s.name.length, 0);
    if (schoolNamesLength <= maxChars) return slicedSchools;

    return [schools[0]];
  }, [schools]);

  const visibleNames = useMemo(() => getVisibleNames(), [getVisibleNames]);
  const amountExtraSchools = schools.length - visibleNames.length;

  const hasMoreSchools = amountExtraSchools > 0;
  return (
    <div className={cn.container}>
      <div className={cn.wrapper}>
        <TitleSmallEmphasized nonTranslate as="h1">
          {schoolName}
        </TitleSmallEmphasized>
      </div>
      <p className={cn.schoolsContainer}>
        {hasMultipleChildren &&
          visibleNames.map((school, schoolIndex) => (
            <span key={school.id}>
              <BodyMediumMidEmphasis
                data-testid="school"
                nonTranslate
                color={colors.slate}
                as="span"
              >
                {school.name}
              </BodyMediumMidEmphasis>
              {schoolIndex < visibleNames.length - 1 && (
                <span className={cn.separator} aria-hidden="true">
                  {' • '}
                </span>
              )}
            </span>
          ))}
        {hasMoreSchools && (
          <>
            <span className={cn.separator} aria-hidden="true">
              {' • '}
            </span>
            <BodyMediumMidEmphasis color={colors.slate} as="span">
              +{amountExtraSchools}
            </BodyMediumMidEmphasis>
          </>
        )}
      </p>
    </div>
  );
};

const cn = {
  container: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 24px;
  `,
  wrapper: css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding-bottom: 4px;
  `,
  separator: css`
    font-size: 14px;
    color: ${colors.slate};
    padding: 0px 4px 0px 4px;
  `,
  schoolsContainer: css`
    display: flex;
    flex-wrap: nowrap;
    overflow: hidden;
    white-space: nowrap;
    text-align: center;
    justify-content: center;
    align-items: center;
    max-width: 90%;
    flex-wrap: wrap;
  `,
};
